import { useEffect, useCallback } from "react";

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function useFocusTrap(ref, active) {
  const handleKeyDown = useCallback((e) => {
    if (e.key !== "Tab" || !ref.current) return;

    const nodes = ref.current.querySelectorAll(FOCUSABLE);
    if (!nodes.length) return;

    const first = nodes[0];
    const last = nodes[nodes.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, [ref]);

  useEffect(() => {
    if (!active || !ref.current) return;

    const container = ref.current;
    const previouslyFocused = document.activeElement;

    // Defer initial focus to next frame so the DOM is settled
    const rafId = requestAnimationFrame(() => {
      if (!container) return;
      const focusables = container.querySelectorAll(FOCUSABLE);
      if (focusables.length) {
        focusables[0].focus();
      }
    });

    container.addEventListener("keydown", handleKeyDown);

    return () => {
      cancelAnimationFrame(rafId);
      container.removeEventListener("keydown", handleKeyDown);
      // Restore focus only if the element is still in the document
      if (previouslyFocused && typeof previouslyFocused.focus === "function" && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [ref, active, handleKeyDown]);
}
